import { ReactComponent as ArrowDownPrimary } from 'assets/icons/sort-arrow-down-primary.svg';
import { ReactComponent as ArrowUpPrimary } from 'assets/icons/sort-arrow-up-primary.svg';
import { ReactComponent as InactiveSort } from 'assets/icons/inactive-sort.svg';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { Table } from 'ui/table/Table';
import * as TableStyled from 'ui/table/Table.styles';

import { DenseTableColumn, DenseTableProps } from './DenseTable.types';
import * as Styled from './DenseTable.styles';

/**
 * The application's table (`ui/table/Table` over shared-ui) driven by a column list.
 *
 * Every DOD list is paginated and sorted server-side, so a header click only reports the requested
 * sort; nothing is reordered here. The sortable header is the same `TableHeaderItemSortable` with
 * the same arrow icons the other pages use - the query-context bound `Table.HeaderSortItem` cannot
 * be reused directly because the monitor keeps its state in the URL, not in the stored queries.
 */
export const DenseTable = <T extends { id: string }>({
  columns,
  data,
  sort,
  onSortChange,
  isLoading,
  nothingToShowText,
  pagination,
  onClickRow,
  activeRowId,
  testIdKey,
  checkboxSelection,
}: DenseTableProps<T>): JSX.Element => {
  const { formatMessage } = useLocale();
  const gridTemplateColumns = columns.map((column) => column.width).join(' ');

  const nextDirection = (column: DenseTableColumn<T>): 'asc' | 'desc' =>
    sort?.key === column.key && sort.direction === 'asc' ? 'desc' : 'asc';

  const renderHeader = (): JSX.Element => (
    <>
      {columns.map((column) =>
        column.sortable && onSortChange ? (
          <TableStyled.TableHeaderItem key={column.key}>
            <TableStyled.TableHeaderItemSortable
              data-testid={`sort-${column.key}`}
              aria-label={formatMessage({
                id: AppMessages[
                  nextDirection(column) === 'asc' ? 'dod.common.sortAscending' : 'dod.common.sortDescending'
                ],
              })}
              onClick={() => onSortChange({ key: column.key, direction: nextDirection(column) })}
            >
              <TableStyled.TableHeaderItemText>{column.header}</TableStyled.TableHeaderItemText>
              {sort?.key !== column.key ? (
                <InactiveSort />
              ) : sort.direction === 'asc' ? (
                <ArrowUpPrimary />
              ) : (
                <ArrowDownPrimary />
              )}
            </TableStyled.TableHeaderItemSortable>
          </TableStyled.TableHeaderItem>
        ) : (
          <Table.HeaderItem key={column.key}>{column.header}</Table.HeaderItem>
        ),
      )}
    </>
  );

  const renderRow = (item: T): JSX.Element => (
    <>
      {columns.map((column) => {
        const content = column.render(item);

        return typeof content === 'string' || typeof content === 'number' ? (
          <Table.RowItem key={column.key} text={String(content)} />
        ) : (
          <Styled.CellContent key={column.key}>{content}</Styled.CellContent>
        );
      })}
    </>
  );

  return (
    <Table<T>
      gridTemplateColumns={gridTemplateColumns}
      data={data}
      renderHeader={renderHeader}
      renderRow={renderRow}
      isLoading={isLoading}
      nothingToShowText={nothingToShowText ?? formatMessage({ id: AppMessages['dod.common.empty'] })}
      pagination={pagination}
      onClickRow={onClickRow}
      activeRowId={activeRowId}
      testIdKey={testIdKey}
      checkboxSelection={checkboxSelection}
    />
  );
};
